import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import AppCircularProgress from "../components/common/AppCircularProgress";

import FiscalItemCard from "../components/fiscal/items/FiscalItemCard";
import CreateFiscalItemModal from "../components/fiscal/items/CreateFiscalItemModal";
import UpdateFiscalItemModal from "../components/fiscal/items/UpdateFiscalItemModal";
import AdjustStockModal from "../components/fiscal/items/AdjustStockModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import {
  getFiscalItems,
  resetFiscalServerStatus,
} from "../redux/actions/fiscalActions";
import PageError from "../components/common/PageError";

const FiscalItemsPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const {
    fiscalItems,
    fiscalItemPagination,
    loadingFiscalItems,
    errorLoadingFiscalItems,
    serverStatus,
    loadingFiscalItemCreate,
    errorCreatingFiscalItem,
    loadingFiscalItemUpdate,
    errorUpdatingFiscalItem,
    loadingStockAdjust,
    errorStockAdjust,
  } = useSelector((state) => state.fiscal);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 12 });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const canRead = hasPermission("product:read_all");
  const canCreate = hasPermission("product:create");
  const canUpdate = hasPermission("product:update");

  const fetchItems = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getFiscalItems({
        page: pageConfig.page,
        limit: pageConfig.limit,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch, canRead]);

  // --- Modal Handlers ---
  const handleOpenCreate = () => setIsCreateOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingFiscalItemCreate) setIsCreateOpen(false);
  }, [loadingFiscalItemCreate]);

  const handleOpenUpdate = (item) => {
    setSelectedItem(item);
    setIsUpdateOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingFiscalItemUpdate) {
      setIsUpdateOpen(false);
      setSelectedItem(null);
    }
  }, [loadingFiscalItemUpdate]);

  const handleOpenAdjust = (item) => {
    setSelectedItem(item);
    setIsAdjustOpen(true);
  };
  const handleCloseAdjust = useCallback(() => {
    if (!loadingStockAdjust) {
      setIsAdjustOpen(false);
      setSelectedItem(null);
    }
  }, [loadingStockAdjust]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  // Handle auto-closing modals on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (status === 200 || status === 201) {
      if (operation === "create-item") setIsCreateOpen(false);
      if (operation === "update-item") setIsUpdateOpen(false);
      if (operation === "adjust-stock") setIsAdjustOpen(false);

      fetchItems();
      dispatch(resetFiscalServerStatus());
    }
  }, [serverStatus, dispatch, fetchItems]);

  const renderContent = () => {
    if (loadingFiscalItems) return <AppCircularProgress />;
    if (errorLoadingFiscalItems)
      return <PageError error={errorLoadingFiscalItems} onClick={fetchItems} />;

    if (!fiscalItems || fiscalItems.length === 0)
      return (
        <Box textAlign="center" mt={5}>
          <Typography color="text.secondary">
            No items registered with eTIMS yet.
          </Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {fiscalItems.map((item) => (
            <Grid item key={item.id} xs={12} sm={6} md={4} lg={3}>
              <FiscalItemCard
                item={item}
                onEdit={
                  canUpdate
                    ? () => {
                        setSelectedItem(item);
                        setIsUpdateOpen(true);
                      }
                    : undefined
                }
                onAdjust={
                  canUpdate
                    ? () => {
                        setSelectedItem(item);
                        setIsAdjustOpen(true);
                      }
                    : undefined
                }
              />
            </Grid>
          ))}
        </Grid>

        {fiscalItemPagination?.totalPages > 1 && (
          <Stack alignItems="center" mt={4}>
            <Pagination
              count={fiscalItemPagination.totalPages}
              page={pageConfig.page}
              onChange={(e, v) =>
                setPageConfig((prev) => ({ ...prev, page: v }))
              }
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="eTIMS Item Mapping"
      subtitle="Map local products to KRA Item Class Codes and synchronize stock limits."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Fiscalize Product
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search product name or eTIMS code..."
      />

      {renderContent()}

      {/* Modals */}
      {isCreateOpen && (
        <CreateFiscalItemModal
          open={handleOpenCreate}
          onClose={handleCloseCreate}
          loading={loadingFiscalItemCreate}
          error={errorCreatingFiscalItem}
        />
      )}

      {isUpdateOpen && selectedItem && (
        <UpdateFiscalItemModal
          open={handleOpenUpdate}
          onClose={handleCloseUpdate}
          item={selectedItem}
          loading={loadingFiscalItemUpdate}
          error={errorUpdatingFiscalItem}
        />
      )}

      {isAdjustOpen && selectedItem && (
        <AdjustStockModal
          open={handleOpenAdjust}
          onClose={handleCloseAdjust}
          item={selectedItem}
          loading={loadingStockAdjust}
          error={errorStockAdjust}
        />
      )}
    </PageContainer>
  );
};
export default FiscalItemsPage;
